import { Injectable, signal } from "@angular/core";
import { Notification, NotificationResponse } from "../../../models/api/notification/notification";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class NotificationsStateService {
  private _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();

  private _notifications = signal<NotificationResponse | null>(null);
  readonly notifications = this._notifications.asReadonly();

  private _realtimeNotification = new Subject<Notification>();
  readonly realtimeNotification = this._realtimeNotification.asObservable();

  /**
   * Sets the standalone unread-notification badge count.
   * @param count The new badge count.
   */
  public setUnreadCount(count: number | undefined) {
    if (count !== undefined) {
      this._unreadCount.set(count);
    }
  }

  /**
   * Replaces the currently loaded notification response.
   * @param notifications The response to store, or `null` to clear the loaded page.
   */
  public setNotifications(notifications: NotificationResponse | null) {
    this._notifications.set(notifications);
  }

  /**
   * Adds a parsed realtime notification to the current state.
   *
   * When a page is loaded, the notification is prepended, the total count is incremented, and the list is trimmed
   * to the configured page size. Response and badge unread counts are incremented only when `readStatus` is `false`.
   * If no page is loaded, only the unread badge can be updated.
   *
   * @param notification The parsed notification received through the WebSocket.
   */
  public addNotificationsRealtime(notification: Notification) {
    const isUnread = notification.readStatus === false;

    this._notifications.update((response) => {
      if (!response) {
        return response;
      }

      const notifications = [notification, ...response.notifications];
      if (response.pageSize !== undefined && notifications.length > response.pageSize) {
        notifications.pop();
      }

      return {
        ...response,
        totalNotifications: response.totalNotifications + 1,
        totalUnreadCount: response.totalUnreadCount + (isUnread ? 1 : 0),
        notifications,
      };
    });

    if (isUnread) {
      this._unreadCount.update((count) => count + 1);
    }

    this._realtimeNotification.next(notification);
  }

  /**
   * Updates the standalone unread-notification badge after a read action.
   * @param all When `true`, resets the badge to zero; otherwise decrements it by one.
   */
  public markAsRead(all: boolean) {
    if (all) {
      this._unreadCount.set(0);
    } else {
      this._unreadCount.update((count) => count - 1);
    }
  }
}
